import { useEffect, useState } from "react";
import { RiArrowDownSLine, RiArrowUpSLine } from "react-icons/ri";
import { Tooltip } from "react-tooltip";
import { IoInformationCircleOutline } from "react-icons/io5";
import { replaceAt } from "../../../util/util";
import { calculateEstimations } from "../domain-utils";
import "../../textinput/textInput.css";
import "./icontools.css";

function DomainInput({
    field,
    setIcontoolsField,
    domainIndex,
    domains,
    n,
    k,
    regex,
    isLocked,
    tooltipMessage,
}) {
    const [isValid, setValid] = useState(true);
    const [getInputValue, setInputValue] = useState("");
    const [getInvalidInputMessage, setInvalidInputMessage] = useState("");

    const determineInvalidInputMessage = (value) => {
        let max = 360;
        let min = -360;
        let rangeIncl = true; // whether the min/max range is excluded or included

        // eslint-disable-next-line
        switch (field) {
            case "center_lat":
                max = 90;
                min = -90;
                rangeIncl = true;
                break;
            case "center_lon":
                max = 180;
                min = -180;
                rangeIncl = true;
                break;
            case "hwidth_lat":
                max = 90;
                min = 0;
                rangeIncl = false;
                break;
            case "hwidth_lon":
                max = 180;
                min = 0;
                rangeIncl = false;
                break;
        }

        if (value.split(".")[1]?.length > 6) {
            return "maximum 6 decimal digits";
        }

        if (rangeIncl) {
            if (value > max) {
                return "can't be greater then: " + max;
            }
            if (value < min) {
                return "can't be smaller then: " + min;
            }
        } else {
            if (value >= max) {
                return "has to be smaller then: " + max;
            }
            if (value <= min) {
                return "has to be greater then: " + min;
            }
        }

        if (getNextEstimation(parseFloat(getNextValue(value))).triangles > 340000000) {
            return "domain cells must not exceed 340M"
        }

        return "not valid input";
    };

    const getNextEstimation = (newValue) => {
        const originalDomain = domains[domainIndex];
        let nextDomain = structuredClone(originalDomain);
        nextDomain.icontools[field] = newValue;
        return calculateEstimations(n, k, domainIndex, domains.toSpliced(domainIndex, 1, nextDomain));
    }

    const getNextValue = (value) => {
        if (value.includes(".")) {
            // if decimal digits exist parse with .toFixed to preserve trailing zeros
            // we do not need to test for 0 here as .toFixed returns a string
            return parseFloat(value).toFixed(value.split(".")[1].length) || undefined;
        } else {
            // we do need to test for 0 with isNaN() here as the logical OR operator (||) considers the number value 0 falsey
            return !isNaN(parseFloat(value)) ? parseFloat(value) : undefined;
        }
    }

    const handleChange = (input) => {
        let value = input.target.value;

        // remove any not allowed characters
        const allowedCharacter = /^[0-9.-]*$/;
        for (let i = 0; i < value.length; i++) {
            if (!value.charAt(i).match(allowedCharacter)) {
                value = replaceAt(value, i, "")
                i-- // compensate index for removed char
            }
        }

        // if (value.match(regex) && getNextEstimation(parseFloat(getNextValue(value))).triangles < 340000000) {
        if (value.match(regex)) {
            setValid(true);
            setIcontoolsField(domainIndex, field, getNextValue(value));
        } else {
            /*
            if the input is not valid the input field will display the getInputValue instead of the state value
            this is to avoid having the state variable be set to an invalid value and also still allow input of invalid values
            blocking any invalid input leads to bad UX
            */
            setInputValue(value || "");
            setValid(false);
            setInvalidInputMessage(determineInvalidInputMessage(value));
        }
    };

    const handleIncrementDecrement = (operation) => {
        let nextValue;
        switch (operation) {
            case "-":
                nextValue = parseFloat(
                    parseFloat(domains[domainIndex].icontools[field] - 1).toFixed(6),
                );
                break;
            case "+":
                nextValue = parseFloat(
                    parseFloat(parseFloat(domains[domainIndex].icontools[field]) + 1).toFixed(6),
                );
                break;
            default:
                console.log("Incrementing or decrementing failed.");
                break;
        }

        if (nextValue.toString().match(regex)) {
            setIcontoolsField(domainIndex, field, nextValue);
        }
    };

    const handleLeave = () => {
        if (domains[domainIndex].icontools[field]) {
            setIcontoolsField(domainIndex, field, parseFloat(domains[domainIndex].icontools[field]));
        }
    };

    useEffect(() => {
        /*
        If the input fields are locked they should set component states to default values.
        This is the case on unrotated domains and global domains.
        In the prior case only the pole fields are locked and need to be reset.
        */
        if (
            (domainIndex === 0 && domains[domainIndex].region_type === 1) ||
            (!domains[domainIndex].icontools.lrotate && (field === "pole_lat" || field === "pole_lon"))
        ) {
            setInputValue("");
            setValid(true);
            setInvalidInputMessage("");
        }
    }, [domains, domainIndex, field]);

    return (
        <div className={isLocked ? "input-container locked field-" + field : "input-container field-" + field}>
            <Tooltip id={"info-domain-tooltip"} />
            <div className={"label-container"}>
                <p>{field}</p>
                <IoInformationCircleOutline
                    data-tooltip-id={"info-domain-tooltip"}
                    data-tooltip-place={"right"}
                    data-tooltip-html={tooltipMessage}
                />
            </div>
            <div className={"domain-input-field-container"}>
                <input
                    value={isValid ? (!isNaN(domains[domainIndex].icontools[field]) ? domains[domainIndex].icontools[field] : "") : getInputValue}
                    inputMode={"decimal"}
                    className={"text-input domain-text-input valid-" + isValid}
                    onChange={handleChange}
                    onBlur={handleLeave}
                    readOnly={isLocked}
                    placeholder={domains[domainIndex][field]}
                />
                <div className={"increment-decrement-container"}>
                    <button
                        onClick={() => handleIncrementDecrement("+")}
                        className={"domain-input-increment"}
                    >
                        <RiArrowUpSLine />
                    </button>
                    <button
                        onClick={() => handleIncrementDecrement("-")}
                        className={"domain-input-decrement"}
                    >
                        <RiArrowDownSLine />
                    </button>
                </div>
            </div>
            {!isValid && (
                <p className={"invalid-input-message"}>{getInvalidInputMessage}</p>
            )}
        </div>
    );
}

export default DomainInput;
